import * as S from './styles';

import Checkbox from '@/components/Common/Checkbox';
import RingLoader from '@/components/Common/RingLoader';
import SearchInput from '@/components/Common/searchInput/SearchInput';

import { DashboardService } from '@/services';
import { DS } from './tokens';

// Template / Adriel hidden teamspaces are not user-assignable.
const HIDDEN_SUBTYPES = new Set(['masterDashboardTemplate', 'bo']);

const toId = (item) => (item && typeof item === 'object' ? item.id : item);

const getMemberCount = (permissions = {}) => {
    const { users = [], teams = [] } = permissions;

    const teamUserIds = teams.flatMap((team) => (team.users ?? []).map(toId));
    const userIds = users.map(toId);

    return new Set([...userIds, ...teamUserIds]).size;
};

export default {
    name: 'TeamspaceAccess',
    props: {
        value: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            loading: true,
            loadFailed: false,
            searchTerm: '',
            teamspaces: [],
        };
    },
    computed: {
        filteredTeamspaces() {
            const { teamspaces, searchTerm } = this;

            if (!searchTerm) return teamspaces;

            const normalizedTerm = searchTerm.toLowerCase();

            return teamspaces.filter((teamspace) =>
                (teamspace.viewTitle ?? '')
                    .toLowerCase()
                    .includes(normalizedTerm)
            );
        },
        isSearchEmpty() {
            const { loading, loadFailed, searchTerm, filteredTeamspaces } =
                this;

            return (
                !loading &&
                !loadFailed &&
                !!searchTerm &&
                filteredTeamspaces.length === 0
            );
        },
        selectableTeamspaces() {
            return this.filteredTeamspaces.filter(
                (teamspace) => !this.checkIsAutoAdd(teamspace)
            );
        },
        isAllSelected() {
            const { selectableTeamspaces } = this;

            return (
                selectableTeamspaces.length > 0 &&
                selectableTeamspaces.every(this.checkIsSelected)
            );
        },
        isPartiallySelected() {
            return (
                !this.isAllSelected &&
                this.selectableTeamspaces.some(this.checkIsSelected)
            );
        },
    },
    methods: {
        // 빈 목록으로는 모드를 구분할 수 없다 — ALL + [] 는 전부 허용, RESTRICTED + [] 는 접근 전무.
        checkIsAutoAdd(teamspace) {
            return (teamspace.dataSourceAccessMode ?? 'ALL') === 'ALL';
        },
        checkIsSelected(teamspace) {
            return this.value.some((selected) => selected.id === teamspace.id);
        },
        toggleRow(teamspace) {
            if (this.checkIsAutoAdd(teamspace)) return;

            const nextValue = this.checkIsSelected(teamspace)
                ? this.value.filter((selected) => selected.id !== teamspace.id)
                : [...this.value, teamspace];

            this.$emit('input', nextValue);
        },
        toggleSelectAll() {
            const { selectableTeamspaces, isAllSelected, value } = this;

            if (isAllSelected) {
                const idsToRemove = new Set(
                    selectableTeamspaces.map((teamspace) => teamspace.id)
                );
                this.$emit(
                    'input',
                    value.filter((selected) => !idsToRemove.has(selected.id))
                );
                return;
            }

            const existingIds = new Set(value.map((selected) => selected.id));
            const merged = [
                ...value,
                ...selectableTeamspaces.filter(
                    (teamspace) => !existingIds.has(teamspace.id)
                ),
            ];

            this.$emit('input', merged);
        },
        renderRowMeta(teamspace) {
            const teamCount = teamspace.permissions?.teams?.length ?? 0;
            const memberCount = getMemberCount(teamspace.permissions);

            return (
                <S.RowMeta>
                    {this.$t('NEW_DASHBOARD.teamspace_list_members_summary', {
                        teams: teamCount,
                        members: memberCount,
                    })}
                </S.RowMeta>
            );
        },
        renderRow(teamspace) {
            const isAutoAdd = this.checkIsAutoAdd(teamspace);
            const selected = isAutoAdd || this.checkIsSelected(teamspace);

            return (
                <S.Row
                    key={teamspace.id}
                    isAutoAdd={isAutoAdd}
                    isSelected={!isAutoAdd && this.checkIsSelected(teamspace)}
                    onClick={() => this.toggleRow(teamspace)}
                >
                    <Checkbox
                        value={selected}
                        hasTooltip={false}
                        disableOnClick
                        bgColor={isAutoAdd ? DS['blue-20'] : DS['blue-50']}
                    />
                    <S.RowNameGroup>
                        <S.RowName>{teamspace.viewTitle}</S.RowName>
                        {isAutoAdd && (
                            <S.AutoAddBadge>
                                {this.$t(
                                    'CONNECTION.teamspace_access_auto_add_badge'
                                )}
                            </S.AutoAddBadge>
                        )}
                    </S.RowNameGroup>
                    {!isAutoAdd && this.renderRowMeta(teamspace)}
                </S.Row>
            );
        },
        renderList() {
            const { loading, loadFailed, isSearchEmpty, filteredTeamspaces } =
                this;

            if (loading) {
                return (
                    <S.LoaderWrapper>
                        <RingLoader />
                    </S.LoaderWrapper>
                );
            }

            if (loadFailed) {
                return (
                    <S.EmptyText>
                        {this.$t('COMMON.error_occurred_try_later')}
                    </S.EmptyText>
                );
            }

            if (isSearchEmpty) {
                return (
                    <S.EmptySearchState>
                        <p>
                            {this.$t(
                                'NEW_DASHBOARD.teamspace_list_no_match_title'
                            )}
                        </p>
                        <p>
                            {this.$t(
                                'CONNECTION.teamspace_access_no_match_description'
                            )}
                        </p>
                    </S.EmptySearchState>
                );
            }

            if (filteredTeamspaces.length === 0) {
                return (
                    <S.EmptyText>
                        {this.$t('NEW_DASHBOARD.teamspace_list_no_access_title')}
                    </S.EmptyText>
                );
            }

            return [
                <S.Row
                    key="select-all"
                    isSelected={this.isAllSelected}
                    isIndeterminate={this.isPartiallySelected}
                    onClick={this.toggleSelectAll}
                >
                    <Checkbox
                        value={this.isAllSelected}
                        indeterminate={this.isPartiallySelected}
                        hasTooltip={false}
                        disableOnClick
                        bgColor={DS['blue-50']}
                    />
                    <S.RowName>{this.$t('CONNECTION.select_all')}</S.RowName>
                </S.Row>,
                ...filteredTeamspaces.map(this.renderRow),
            ];
        },
    },
    async mounted() {
        try {
            const teamspaces = await DashboardService.getTeamspacesManagement();
            this.teamspaces = (teamspaces ?? []).filter(
                (teamspace) => !HIDDEN_SUBTYPES.has(teamspace.subtype)
            );
        } catch (error) {
            console.error(error);
            this.loadFailed = true;
        } finally {
            this.loading = false;
        }
    },
    render() {
        return (
            <S.Wrapper>
                <S.Title>
                    {this.$t('CONNECTION.teamspace_access_title')}
                </S.Title>
                <S.Description>
                    {this.$t('CONNECTION.teamspace_access_description')}
                </S.Description>
                <S.DropdownAnchor>
                    <S.SearchInputWrapper>
                        <SearchInput
                            value={this.searchTerm}
                            placeholder={this.$t(
                                'CONNECTION.teamspace_access_search_placeholder'
                            )}
                            onInput={(v) => (this.searchTerm = v)}
                        />
                    </S.SearchInputWrapper>
                    <S.ListViewport isSearchEmpty={this.isSearchEmpty}>
                        {this.renderList()}
                    </S.ListViewport>
                </S.DropdownAnchor>
            </S.Wrapper>
        );
    },
};
